import useIdentity from "@/features/ui/hooks/use-identity";
import { DEFAULT_LIST_LINE_SHEET_PAIRS_PAGE_SIZE } from "@/features/line-sheet-sets/hooks/use-list-line-sheet-set-pairs";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Switch,
  Text,
  useToast,
} from "@chakra-ui/react";
import AppCompanySelect from "@/features/line-sheet-sets/app-company-select";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppPagination from "@/features/line-sheet-sets/app-pagination";
import React, { useEffect, useMemo, useState } from "react";
import BuyerLineSheetSetGrid from "@/features/order-sheet-sets/buyer-line-sheet-set-grid";
import { useRecoilValue } from "recoil";
import { buyerLineSheetSetRowsMutateId } from "@/features/order-sheet-sets/recoils/buyer-line-sheet-set-rows-state";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useBuyerLineSheetSetRows from "@/features/order-sheet-sets/hooks/use-buyer-line-sheet-set-rows";
import AppListHeader from "@/features/ui/app-list-header";
import useListDenseLineSheetSets, {
  ListDenseLineSheetSetsPathParameter,
  ListDenseLineSheetSetsRequest,
  ListDenseLineSheetSetsResponse,
} from "@/features/line-sheet-sets/hooks/use-list-dense-line-sheet-sets";
import AppQueryController from "@/features/ui/app-query-controller";
import { toTitle } from "@/utils/case";
import AppSeasonMultiSelect from "@/features/line-sheet-sets/app-season-multi-select";
import { OrderByItem } from "@/features/types";
import useTypedSearchParams from "@/features/invoices/hooks/use-typed-search-params";
import { PersistedLineSheetSetListQuery } from "@/features/order-sheet-sets/agency-line-sheet-set-list-page";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import SVG from "react-inlinesvg";
import FilterModal from "@/features/order-sheet-sets/FilterModal";
import ChipList from "@/features/order-sheet-sets/ChipList";
import BrandRequestModal from "@/shared/brand-request-modal";
import useGetCompanies from "@/features/order-sheet-sets/hooks/useGetCompanies";
import { Suggestion } from "@/features/ui/app-autocomplete-input";
import AppQueryControllerAutoComplete from "@/features/ui/app-query-controller-auto-complete";
import { useSearchParams } from "react-router-dom";

export default function BuyerLineSheetSetListPage() {
  const identity = useIdentity();
  const { setRows } = useBuyerLineSheetSetRows();
  const rowsMutateId = useRecoilValue(buyerLineSheetSetRowsMutateId);
  const { error: showError } = useAppToasts();
  const [query, setQuery] =
    useTypedSearchParams<PersistedLineSheetSetListQuery>();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [queryCount, setQueryCount] = useState(0);
  const [isBrandRequestModalOpen, setIsBrandRequestModalOpen] = useState(false);
  const { t, tTitle, tSentence } = useI18nHelper();
  const totalBrandList = useGetCompanies(["BRAND"]) ?? [];
  const totalBoutiqueList = useGetCompanies(["BOUTIQUE"]) ?? [];

  const toast = useToast();
  let inputValue: Suggestion;
  if (query?.query) {
    inputValue = {
      value: query.query,
      type: "custom",
      label: query.query,
    };
  } else {
    inputValue = {
      value: "",
      type: "custom",
      label: "",
    };
  }

  let suggestions: Suggestion[] = totalBrandList.map((brand) => ({
    type: "Brand",
    label: brand.name,
    value: brand.id.toString(),
    // 'EMPORIO ARMANI', '프라다' 같은 유사 표기로도 찾게 한다.
    keywords: brand.similarWords,
  }));
  suggestions = suggestions.concat(
    totalBoutiqueList.map((boutique) => ({
      type: "Boutique",
      label: boutique.name,
      value: boutique.id.toString(),
    }))
  );

  const listDenseLineSheetSetsKey = useMemo(():
    | {
        parameter: ListDenseLineSheetSetsPathParameter;
        request: ListDenseLineSheetSetsRequest;
      }
    | undefined => {
    if (identity?.company) {
      return {
        parameter: {
          by: identity.company.type,
          companyId: identity.company.id,
        },
        request: {
          ...query,
          pageSize: DEFAULT_LIST_LINE_SHEET_PAIRS_PAGE_SIZE,
          pageNumber: query.pageNumber || 1,
          orderBy: [new OrderByItem("publishedAt", "descend")],
          isPublished__eq: true,
        },
      };
    }
  }, [identity, query]);

  const {
    isLoading: isDenseLineSheetSetsLoading,
    data: listDenseLineSheetSetsData,
    mutate: mutateListDenseLineSheetSets,
    error: listDenseLineSheetSetsError,
  } = useListDenseLineSheetSets(
    listDenseLineSheetSetsKey?.parameter,
    listDenseLineSheetSetsKey?.request,
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  useEffect(() => {
    setRows(listDenseLineSheetSetsData?.lineSheetSets);
  }, [listDenseLineSheetSetsData, setRows]);

  useEffect(() => {
    return () => {
      mutateListDenseLineSheetSets((prev) => prev, false);
    };
  }, [mutateListDenseLineSheetSets]);

  useEffect(() => {
    if (rowsMutateId > 0) {
      mutateListDenseLineSheetSets();
    }
  }, [mutateListDenseLineSheetSets, rowsMutateId]);

  const isWorkingDraftOnly = !!(
    query["orderSheetSet.status__in"]?.length === 1 &&
    query["orderSheetSet.status__in"].includes("WORKING_DRAFT")
  );

  return (
    <Flex
      flexDirection={"column"}
      gap={"12px"}
      height={"100%"}
      paddingBottom={"16px"}
    >
      <Box>
        <Flex
          justifyContent={"space-between"}
          alignItems={"center"}
          paddingX={"16px"}
          height={"48px"}
          borderBottom={"1px solid #D4D4D1"}
          background={"#FFFFFF"}
        >
          <Text textStyle={"heading-sm"}>
            {tTitle(
              query.type__eq === "SEASON" ? "pre_order" : "navigation.stock"
            )}
          </Text>
          <Flex gap={"8px"} key={"AgencyLineSheetSetListFilter_RequestBrand"}>
            <Text textStyle={"body-sm-regular"} color={"#444444"}>
              {tSentence("request_brand_list_title")}
            </Text>
            <Text
              textStyle={"body-sm-bold"}
              color={"#444444"}
              textDecoration={"underline"}
              cursor={"pointer"}
              onClick={() => {
                setIsBrandRequestModalOpen(true);
              }}
            >
              {tSentence("request_brand_list_subtitle")}
            </Text>
          </Flex>
        </Flex>
        <AppListHeader
          filters={[
            <AppQueryControllerAutoComplete
              key={"AgencyLineSheetSetListFilter_Query"}
              value={
                inputValue.type === "custom"
                  ? inputValue
                  : {
                      value: "",
                      type: "custom",
                      label: "",
                    }
              }
              showButton={true}
              placeholder={t("search_input_placeholder")}
              leadingIcon="search"
              showIconsAndBtn={true}
              width="400px"
              suggestions={suggestions}
              onChange={(value) => {
                if (
                  value.value &&
                  value.type !== "Brand" &&
                  value.type !== "Boutique" &&
                  value.value.length < 3
                ) {
                  toast({
                    title: "검색어는 3글자 이상 입력해주세요.",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                    position: "top",
                  });
                  return;
                }
                setQuery((prev) => {
                  if (value.type === "Brand") {
                    // 이미 선택된 브랜드일 경우 무시
                    if (
                      prev["madeByCompany.id__in"]
                        ?.map((id) => String(id))
                        .includes(value.value)
                    ) {
                      return prev;
                    }
                    return {
                      ...prev,
                      "made_by_company.id__in": value.value,
                      query: "",
                      pageNumber: 1,
                    };
                  } else if (value.type === "Boutique") {
                    // 이미 선택된 경우 무시
                    if (
                      prev["issuedByCompany.id__in"]
                        ?.map((id) => String(id))
                        .includes(value.value)
                    ) {
                      return prev;
                    }
                    return {
                      ...prev,
                      "issued_by_company.id__in": value.value,
                      query: "",
                      pageNumber: 1,
                    };
                  } else {
                    return {
                      ...prev,
                      query: value.value,
                      pageNumber: 1,
                    };
                  }
                });
              }}
            />,
            <Flex key={"AgencyLineSheetSetListFilters"} gap={"12px"}>
              <AppCompanySelect
                key={"AgencyLineSheetSetListFilter_Brand"}
                minWidth={"80px"}
                width={"80px"}
                name={toTitle(t("common:brand"))}
                types={["BRAND"]}
                id={query["madeByCompany.id__in"]}
                isMultiple={true}
                isClearable={false}
                showSelectedBorder={true}
                showCount={true}
                onSelect={(companies) => {
                  setQuery((prev) => {
                    return {
                      ...prev,
                      "madeByCompany.id__in": companies?.map(
                        (company) => company.id
                      ),
                      pageNumber: 1,
                    };
                  });
                }}
              />
              {/*<AppSeasonMultiSelect*/}
              {/*  key={"season"}*/}
              {/*  value={query.season__in}*/}
              {/*  width={"80px"}*/}
              {/*  onSelect={(value) => {*/}
              {/*    setQuery((prev) => {*/}
              {/*      return {*/}
              {/*        ...prev,*/}
              {/*        season__in: value,*/}
              {/*        pageNumber: 1,*/}
              {/*      };*/}
              {/*    });*/}
              {/*  }}*/}
              {/*/>*/}
              <AppCompanySelect
                key={"AgencyLineSheetSetListFilter_IssuedBy"}
                name={toTitle(t("supplier"))}
                minWidth={"80px"}
                types={["BOUTIQUE"]}
                width={"80px"}
                id={query["issuedByCompany.id__in"]}
                isMultiple={true}
                isClearable={false}
                showSelectedBorder={true}
                showCount={true}
                onSelect={(companies) => {
                  setQuery((prev) => {
                    return {
                      ...prev,
                      "issuedByCompany.id__in": companies?.map(
                        (company) => company.id
                      ),
                      pageNumber: 1,
                    };
                  });
                }}
              />
              <FormControl
                display="flex"
                alignItems="center"
                width={"fit-content"}
              >
                <FormLabel htmlFor="working-draft-only" mb="0" mr={"6px"}>
                  <Text
                    textStyle={"body-sm-regular"}
                    color={"#444444"}
                    whiteSpace={"nowrap"}
                  >
                    {tSentence("view_working_draft_only")}
                  </Text>
                </FormLabel>
                <Switch
                  id="working-draft-only"
                  isChecked={isWorkingDraftOnly}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setQuery((prev) => ({
                      ...prev,
                      "orderSheetSet.status__in": checked
                        ? ["WORKING_DRAFT"]
                        : undefined,
                      pageNumber: 1,
                    }));
                  }}
                />
              </FormControl>
              {/* 단품오더는 스탁오더에서만 되는 개념이라 프리오더 탭에서는 감춘다. */}
              {query.type__eq === "STOCK" && (
                <FormControl
                  display="flex"
                  alignItems="center"
                  width={"fit-content"}
                >
                  <FormLabel htmlFor="by-unit-order-only" mb="0" mr={"6px"}>
                    <Text
                      textStyle={"body-sm-regular"}
                      color={"#444444"}
                      whiteSpace={"nowrap"}
                    >
                      {tSentence("view_by_unit_order_only")}
                    </Text>
                  </FormLabel>
                  <Switch
                    id="by-unit-order-only"
                    isChecked={!!query.pathpickOpen__eq}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setQuery((prev) => ({
                        ...prev,
                        pathpickOpen__eq: checked ? true : undefined,
                        pageNumber: 1,
                      }));
                    }}
                  />
                </FormControl>
              )}
            </Flex>,
            // <Button
            //   key={"FilterButton"}
            //   padding={"4px 8px"}
            //   height="28px"
            //   display="flex"
            //   gap={"4px"}
            //   variant={"outline"}
            //   onClick={() => setIsFilterModalOpen(true)}
            //   minWidth={"82px"}
            //   border={`${
            //     queryCount > 0 ? "1px solid #1272EF" : "1px solid #8F8F8C"
            //   }`}
            // >
            //   <SVG
            //     src={"/icons-v1_1/filter.svg"}
            //     width={"16px"}
            //     height={"16px"}
            //     color={"#444444"}
            //     style={{
            //       minWidth: "16px",
            //     }}
            //   />
            //   <Text textStyle={"body-sm-bold"}>
            //     {tTitle("filter")} {queryCount > 0 && queryCount}
            //   </Text>
            // </Button>,
            () => {
              if (queryCount <= 0) return;
              return (
                <AppButton
                  key={"AgencyLineSheetSetListFilter_ResetFilter"}
                  variant={"ghost"}
                  size={"medium"}
                  onClick={() => {
                    setQuery({
                      type__eq: query.type__eq,
                      pageNumber: 1,
                    });
                  }}
                  width={"72px"}
                  height={"28px"}
                >
                  <Flex gap={"4px"} alignItems={"center"}>
                    <SVG
                      src={"/icons-v1_1/arrow-rotate.svg"}
                      width={"16px"}
                      height={"16px"}
                      color={"#8F8F8C"}
                    />
                    <Text textStyle="body-sm-bold" color={"#444444"}>
                      {t("clear_all")}
                    </Text>
                  </Flex>
                </AppButton>
              );
            },
            () => {
              if (queryCount <= 0) return;
              return (
                <Text key={"Divider"} color={"#D4D4D1"}>
                  |
                </Text>
              );
            },
            <ChipList key={"ChipList"} query={query} setQuery={setQuery} />,
          ]}
          actions={[]}
          showFilterText={false}
        />
      </Box>
      <Box
        flexGrow={1}
        overflow={"auto"}
        style={{
          scrollbarGutter: "stable",
        }}
      >
        <BuyerLineSheetSetGrid denseLineSheet={listDenseLineSheetSetsData} />
      </Box>
      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        queryCount={queryCount}
        setQueryCount={(count) => setQueryCount(count)}
        query={query}
        setQuery={setQuery}
        resetBtn={
          <AppButton
            key={"AgencyLineSheetSetListFilter_ResetFilter"}
            variant={"ghost"}
            size={"medium"}
            onClick={() => {
              setQuery({
                pageNumber: 1,
              });
            }}
            width={"72px"}
            height={"28px"}
          >
            <Flex gap={"4px"} alignItems={"center"}>
              <SVG
                src={"/icons-v1_1/arrow-rotate.svg"}
                width={"16px"}
                height={"16px"}
                color={"#8F8F8C"}
              />
              <Text textStyle="body-sm-bold" color={"#444444"}>
                {t("clear_all")}
              </Text>
            </Flex>
          </AppButton>
        }
      >
        <Box>
          <Flex flexDirection="column" gap={"24px"}>
            <Flex flexDirection={"column"} gap={"6px"}>
              <Text>{tTitle("brand")}</Text>
              <AppCompanySelect
                key={"AgencyLineSheetSetListFilter_Brand"}
                name={toTitle(t("common:brand"))}
                types={["BRAND"]}
                id={query["madeByCompany.id__in"]}
                isMultiple={true}
                onSelect={(companies) => {
                  setQuery((prev) => {
                    return {
                      ...prev,
                      "madeByCompany.id__in": companies?.map(
                        (company) => company.id
                      ),
                      pageNumber: 1,
                    };
                  });
                }}
              />
            </Flex>
            <Flex flexDirection={"column"} gap={"6px"}>
              <Text>{tTitle("season")}</Text>
              <AppSeasonMultiSelect
                key={"season"}
                value={query.season__in}
                onSelect={(value) => {
                  setQuery((prev) => {
                    return {
                      ...prev,
                      season__in: value,
                      pageNumber: 1,
                    };
                  });
                }}
              />
            </Flex>
            <Flex flexDirection={"column"} gap={"6px"}>
              <Text>{tTitle("issued_by")}</Text>
              <AppCompanySelect
                key={"AgencyLineSheetSetListFilter_IssuedBy"}
                name={toTitle(t("issued_by"))}
                types={["BOUTIQUE"]}
                id={query["issuedByCompany.id__in"]}
                isMultiple={true}
                onSelect={(companies) => {
                  setQuery((prev) => {
                    return {
                      ...prev,
                      "issuedByCompany.id__in": companies?.map(
                        (company) => company.id
                      ),
                      pageNumber: 1,
                    };
                  });
                }}
              />
            </Flex>
          </Flex>
        </Box>
      </FilterModal>
      <BrandRequestModal
        isOpen={isBrandRequestModalOpen}
        onClose={() => setIsBrandRequestModalOpen(false)}
      />
      <Flex flexDirection={"row"} justifyContent={"center"}>
        <AppPagination
          capacity={10}
          pageSize={DEFAULT_LIST_LINE_SHEET_PAIRS_PAGE_SIZE}
          totalCount={listDenseLineSheetSetsData?.totalCount}
          pageNumber={query.pageNumber}
          setPageNumber={(pageNumber) =>
            setQuery((prev) => {
              return {
                ...prev,
                pageNumber,
              };
            })
          }
        />
      </Flex>
    </Flex>
  );
}
